import { useState } from "react";

/**
 * Drawing-tools toolbar that sits over the chart's top-right corner. Each
 * button activates a klinecharts overlay (the user then clicks on the chart
 * to place the drawing's points). "Clear all" removes every drawing.
 *
 * We expose a curated set rather than every klinecharts built-in — the long
 * tail (Gann box, Elliott waves, harmonic patterns) is available but rarely
 * used day-to-day, so it's hidden under a "More" dropdown to keep the
 * primary toolbar uncluttered.
 */

interface ToolDef {
  name: string;
  label: string;
  description: string;
}

const COMMON_TOOLS: ToolDef[] = [
  {
    name: "priceRangeMeasure",
    label: "📏",
    description: "Measure: price change %, bar count, time delta",
  },
  {
    name: "volumeProfile",
    label: "📊",
    description: "Volume profile: POC, VAH, VAL histogram",
  },
  {
    name: "weeklyVolumeProfile",
    label: "📅",
    description:
      "Periodic volume profile + HTP (POC) line. Defaults to weekly. Right-click → Edit profile… to switch period (daily / weekly / monthly / quarterly) and change row count.",
  },
  { name: "segment", label: "/", description: "Trend line" },
  { name: "rayLine", label: "→", description: "Ray (extends one direction)" },
  { name: "horizontalStraightLine", label: "—", description: "Horizontal line" },
  { name: "verticalStraightLine", label: "|", description: "Vertical line" },
  { name: "rectangle", label: "▭", description: "Rectangle / zone" },
  { name: "fibonacciLine", label: "Fib", description: "Fibonacci retracement" },
  { name: "parallelStraightLine", label: "‖", description: "Parallel channel" },
  { name: "text", label: "T", description: "Text annotation" },
  { name: "arrow", label: "↗", description: "Arrow" },
];

const MORE_TOOLS: ToolDef[] = [
  { name: "horizontalRayLine", label: "Horizontal ray", description: "Horizontal ray" },
  { name: "horizontalSegment", label: "Horizontal segment", description: "Horizontal segment" },
  { name: "verticalRayLine", label: "Vertical ray", description: "Vertical ray" },
  { name: "verticalSegment", label: "Vertical segment", description: "Vertical segment" },
  { name: "straightLine", label: "Straight line", description: "Infinite line" },
  { name: "fibonacciSegment", label: "Fib segment", description: "Fibonacci segment" },
  { name: "fibonacciExtension", label: "Fib extension", description: "Fibonacci extension" },
  { name: "fibonacciCircle", label: "Fib circle", description: "Fibonacci circle" },
  { name: "fibonacciSpiral", label: "Fib spiral", description: "Fibonacci spiral" },
  { name: "fibonacciSpeedResistanceFan", label: "Fib fan", description: "Fibonacci fan" },
  { name: "gannBox", label: "Gann box", description: "Gann box" },
  { name: "priceLine", label: "Price line", description: "Price-marker line" },
  { name: "priceChannelLine", label: "Price channel", description: "Price channel" },
  { name: "threeWaves", label: "3 Waves", description: "Three-wave pattern" },
  { name: "fiveWaves", label: "5 Waves", description: "Five-wave pattern" },
  { name: "eightWaves", label: "8 Waves", description: "Eight-wave pattern" },
  { name: "abcd", label: "ABCD", description: "ABCD pattern" },
  { name: "xabcd", label: "XABCD", description: "XABCD harmonic" },
  { name: "triangle", label: "Triangle", description: "Triangle" },
  { name: "circle", label: "Circle", description: "Circle" },
  { name: "weightedAverageLine", label: "VWAP line", description: "Weighted-average line" },
  { name: "simpleAnnotation", label: "Annotation", description: "Simple annotation marker" },
];

interface Props {
  onTool: (toolName: string) => void;
  onClearAll: () => void;
}

export default function DrawingToolbar({ onTool, onClearAll }: Props) {
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-surface border border-border rounded shadow-sm px-1 py-1">
      {COMMON_TOOLS.map((t) => (
        <button
          key={t.name}
          type="button"
          onClick={() => onTool(t.name)}
          title={t.description}
          className="w-7 h-7 flex items-center justify-center text-sm font-medium text-fg hover:bg-surface-2 rounded"
        >
          {t.label}
        </button>
      ))}
      <div className="relative">
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          title="More tools"
          className="w-7 h-7 flex items-center justify-center text-sm font-medium text-fg hover:bg-surface-2 rounded"
        >
          …
        </button>
        {moreOpen && (
          <div
            className="absolute right-0 top-full mt-1 w-52 max-h-72 overflow-y-auto bg-surface border border-border rounded shadow-lg z-30"
            onMouseLeave={() => setMoreOpen(false)}
          >
            {MORE_TOOLS.map((t) => (
              <button
                key={t.name}
                type="button"
                onClick={() => {
                  onTool(t.name);
                  setMoreOpen(false);
                }}
                className="block w-full text-left px-3 py-1.5 text-xs text-fg hover:bg-surface-2"
                title={t.description}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="w-px h-5 bg-border mx-1" />
      <button
        type="button"
        onClick={onClearAll}
        title="Clear all drawings"
        className="px-2 h-7 text-xs font-medium text-down-600 dark:text-down-400 hover:bg-down-50 dark:hover:bg-down-500/10 rounded"
      >
        Clear
      </button>
    </div>
  );
}
